import React, { ReactElement, ReactNode, useRef, useState } from 'react';
import { arrow, autoUpdate, flip, FloatingPortal, offset, shift, useClick, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole } from '@floating-ui/react';
import classNames from 'classnames';
import useEscapeKey from '@frontastic-engbers/helpers/hooks/useEscapeKey';
import styles from './tooltip.module.scss';
interface TooltipProps {
  text: ReactElement;
  children: ReactNode;
  direction: 'top' | 'bottom' | 'left' | 'right';
  id: string;
  horizontalPosition?: 'left' | 'center' | 'right';
  fontSize?: 'normal' | 'small';
  withTrigger?: boolean;
  className?: string;
}
const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  direction,
  id,
  horizontalPosition = 'center',
  fontSize = 'normal',
  withTrigger = true,
  className
}) => {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  useEscapeKey(() => setIsOpen(false), isOpen);
  const {
    refs,
    floatingStyles,
    context,
    middlewareData,
    placement
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: direction,
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip(), shift(), arrow({
      element: arrowRef
    })]
  });
  const hover = useHover(context, {
    move: false
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'tooltip'
  });
  const click = useClick(context);
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([hover, focus, dismiss, role, click]);
  const triggerProps = {
    ...getReferenceProps(),
    'aria-describedby': isOpen ? id : undefined,
    'aria-haspopup': true,
    'aria-expanded': isOpen
  };
  const {
    x: arrowX,
    y: arrowY
  } = middlewareData.arrow ?? {};
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
  }[placement.split('-')[0]] ?? '';
  return <div className="relative inline-block" data-sentry-component="Tooltip" data-sentry-source-file="index.tsx">
      {withTrigger ? <button type="button" ref={refs.setReference} {...triggerProps} className={classNames('inline-flex items-center justify-center', className)}>
          {children}
        </button> : <div ref={refs.setReference} {...triggerProps}>
          {children}
        </div>}

      <FloatingPortal data-sentry-element="FloatingPortal" data-sentry-source-file="index.tsx">
        {isOpen && <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()} className={classNames(styles.tooltip, horizontalPosition === 'left' && '-left-1/4', horizontalPosition === 'center' && 'left-1/2 -translate-x-2/4', horizontalPosition === 'right' && '-right-1/4')} id={id}>
            <div className={classNames(styles.tooltipContent, fontSize === 'small' ? 'text-xs' : 'text-sm')}>
              {text}
            </div>

            <div ref={arrowRef} className={styles.tooltipArrowWrapper} style={{
          left: arrowX != null ? `${arrowX}px` : undefined,
          top: arrowY != null ? `${arrowY}px` : undefined,
          [staticSide]: '-5px'
        }}>
              <div className={styles.tooltipArrowShadow} />
              <div className={styles.tooltipArrow} />
            </div>
          </div>}
      </FloatingPortal>
    </div>;
};
export default Tooltip;